import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { CircleHelp, Building2, MonitorDown, ShieldCheck, Inbox, Brain, Bell, MailCheck, Users, ArrowRight, GraduationCap } from "lucide-react";

export const STEPS = [
  { id: "customer", icon: Building2, title: "Onboard a customer", where: "Dashboard → + New customer", how: "Each MSP customer gets an isolated tenant with its own enrollment key and response policy. Use the switcher (top-left) to move between customers." },
  { id: "agent", icon: MonitorDown, title: "Enroll machines", where: "Settings → Live Agent", how: "Copy the one-line Windows installer and run it in an Admin PowerShell on any endpoint. It installs as a SYSTEM service, survives reboots and starts streaming processes, posture and Windows Event Logs within 10 seconds." },
  { id: "policy", icon: ShieldCheck, title: "Set the autonomy level", where: "Settings → AI Response Policy", how: "Level 1 observes, Level 2 proposes actions for a human to approve, Level 3 auto-isolates ransomware at >95% confidence. Start with L2 and graduate to L3 once you trust it." },
  { id: "approvals", icon: Inbox, title: "Approve or reject AI proposals", where: "Sidebar → Approval Inbox", how: "When L3 is off, the AI files proposals here. One click executes the action and writes the audit trail. Unanswered proposals escalate to critical after 15 minutes." },
  { id: "ai", icon: Brain, title: "Ask the AI SOC analyst", where: "AI Incidents → open an incident", how: "Generate a briefing on GPT-5.4, Claude Sonnet 5 or Perplexity — or hit Compare to run all three side by side." },
  { id: "slack", icon: Bell, title: "Wire up Slack", where: "Settings → Slack Alerts", how: "Paste a Bot Token + Signing Secret to get Approve / Reject buttons directly in your channel, or an Incoming Webhook for link buttons." },
  { id: "digest", icon: MailCheck, title: "Daily digest & security contact", where: "Settings → Customer Management / Daily SOC Digest", how: "Set the customer's security contact; every morning at 07:00 UTC they receive a branded summary. Preview or send it now anytime." },
  { id: "portal", icon: Users, title: "Give customers a portal login", where: "Settings → Customer Portal", how: "Invite a customer user by email. They get a one-time temporary password (shown to you once), see only their own data, and cannot take actions." },
];

export function HelpTip({ step, side = "bottom", children }) {
  const s = STEPS.find((x) => x.id === step);
  return (
    <Popover>
      <PopoverTrigger asChild>
        {children || <button data-testid={`help-${step}`} aria-label="How to" className="text-zinc-500 hover:text-cyan-300 transition-colors duration-150"><CircleHelp size={14} /></button>}
      </PopoverTrigger>
      <PopoverContent side={side} className="w-80 bg-[#1B1B28] border-white/10 rounded-none text-zinc-100 p-4">
        <div className="flex items-center gap-2 text-cyan-300 font-mono text-[10px] uppercase tracking-widest"><s.icon size={12} /> How to · {s.title}</div>
        <div className="text-[10px] font-mono text-zinc-500 mt-1">{s.where}</div>
        <p className="text-xs text-zinc-300 mt-2 leading-relaxed">{s.how}</p>
      </PopoverContent>
    </Popover>
  );
}

const ROUTES = { customer: "/admin", agent: "/admin/settings", policy: "/admin/settings", approvals: "/admin/approvals", ai: "/admin/incidents", slack: "/admin/settings", digest: "/admin/settings", portal: "/admin/settings" };

export function AdminTour({ stats, tenant, forceOpen, onClose }) {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [i, setI] = useState(0);
  useEffect(() => { if (!localStorage.getItem("cds_tour_done")) setOpen(true); }, []);
  useEffect(() => { if (forceOpen) { setI(0); setOpen(true); } }, [forceOpen]);
  const close = () => { localStorage.setItem("cds_tour_done", "1"); setOpen(false); onClose?.(); };
  const s = STEPS[i];
  const done = doneMap(stats, tenant);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent data-testid="admin-tour" className="bg-[#1B1B28] border-white/10 text-zinc-100 sm:max-w-[560px] rounded-none">
        <DialogHeader>
          <DialogTitle className="font-display font-extrabold tracking-tight flex items-center gap-2"><GraduationCap size={16} className="text-cyan-300" /> Admin quick start · {i + 1} / {STEPS.length}</DialogTitle>
          <DialogDescription className="text-zinc-500 text-xs">Everything an MSP admin needs to run CyboScan, in order. Reopen anytime from the Getting Started card.</DialogDescription>
        </DialogHeader>
        <div className="flex gap-1">{STEPS.map((x, k) => <button key={x.id} onClick={() => setI(k)} className={`h-1 flex-1 ${k === i ? "bg-cyan-400" : done[x.id] ? "bg-emerald-500/60" : "bg-white/10"}`} />)}</div>
        <div className="border hairline p-4 min-h-[150px]">
          <div className="flex items-center gap-2 text-cyan-300 font-mono text-[10px] uppercase tracking-widest"><s.icon size={12} /> Step {i + 1} {done[s.id] && <span className="text-emerald-400">· done</span>}</div>
          <div className="font-semibold text-lg mt-1">{s.title}</div>
          <div className="text-[10px] font-mono text-zinc-500">{s.where}</div>
          <p className="text-sm text-zinc-300 mt-2 leading-relaxed">{s.how}</p>
        </div>
        <div className="flex items-center justify-between gap-2">
          <button data-testid="tour-skip" onClick={close} className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 hover:text-white">Skip tour</button>
          <div className="flex gap-2">
            <button data-testid="tour-goto" onClick={() => { close(); nav(ROUTES[s.id]); }} className="border hairline text-zinc-300 hover:bg-white/5 px-3 py-1.5 text-[10px] font-mono uppercase tracking-widest flex items-center gap-1">Take me there <ArrowRight size={11} /></button>
            {i > 0 && <button data-testid="tour-prev" onClick={() => setI(i - 1)} className="border hairline text-zinc-300 hover:bg-white/5 px-3 py-1.5 text-[10px] font-mono uppercase tracking-widest">Back</button>}
            {i < STEPS.length - 1
              ? <button data-testid="tour-next" onClick={() => setI(i + 1)} className="bg-cyan-400 text-[#15151F] hover:bg-cyan-300 px-3 py-1.5 text-[10px] font-mono uppercase tracking-widest font-bold">Next</button>
              : <button data-testid="tour-finish" onClick={close} className="bg-cyan-400 text-[#15151F] hover:bg-cyan-300 px-3 py-1.5 text-[10px] font-mono uppercase tracking-widest font-bold">Finish</button>}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function doneMap(stats, tenant) {
  return {
    customer: true,
    agent: (stats?.agent_endpoints || 0) > 0,
    policy: !!stats?.policy_touched,
    approvals: (stats?.approvals_decided || 0) > 0,
    ai: (stats?.ai_briefings || 0) > 0,
    slack: !!stats?.slack_enabled,
    digest: !!tenant?.contact_email,
    portal: (stats?.portal_users || 0) > 0,
  };
}

export function GettingStarted({ stats, tenant, onOpenTour }) {
  const nav = useNavigate();
  const done = doneMap(stats, tenant);
  const count = Object.values(done).filter(Boolean).length;
  return (
    <div data-testid="getting-started" className="border hairline bg-[#1B1B28]">
      <div className="px-4 py-3 border-b hairline flex items-center justify-between">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Getting started · {tenant?.name}</div>
          <div className="font-display font-bold text-sm">{count}/{STEPS.length} setup steps complete</div>
        </div>
        <button data-testid="open-tour" onClick={onOpenTour} className="border border-cyan-400/50 text-cyan-300 hover:bg-cyan-400/10 px-2.5 py-1 text-[10px] font-mono uppercase tracking-widest flex items-center gap-1"><GraduationCap size={12} /> Tour</button>
      </div>
      <div className="h-1 bg-white/5"><div className="h-1 bg-emerald-400 transition-[width] duration-500" style={{ width: `${(count / STEPS.length) * 100}%` }} /></div>
      <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-y divide-white/5">
        {STEPS.map((s) => (
          <div key={s.id} data-testid={`gs-${s.id}`} className="p-3 flex items-start gap-2 min-w-0">
            <span className={`w-4 h-4 border flex items-center justify-center text-[9px] shrink-0 mt-0.5 ${done[s.id] ? "border-emerald-400 text-emerald-400" : "border-white/20 text-transparent"}`}>✓</span>
            <div className="min-w-0 flex-1">
              <button onClick={() => nav(ROUTES[s.id])} className="text-xs font-medium text-left hover:text-cyan-300 truncate block w-full">{s.title}</button>
              <div className="text-[10px] text-zinc-500 truncate">{s.where}</div>
            </div>
            <HelpTip step={s.id} side="top" />
          </div>
        ))}
      </div>
    </div>
  );
}
